import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from './OnboardingLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Package, Users, TrendingUp, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const OnboardingDone = () => {
  const navigate = useNavigate();

  useEffect(() => {
    completeOnboarding();
  }, []);

  const completeOnboarding = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/onboarding/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Complete onboarding error:', error);
    }
  };

  const quickLinks = [
    {
      icon: Package,
      title: 'Start Managing Deliveries',
      description: 'View and track all your deliveries in real-time',
      action: () => navigate('/deliveries'),
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Users,
      title: 'Manage Your Team',
      description: 'Add more drivers and manage staff',
      action: () => navigate('/admin/staff'),
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: TrendingUp,
      title: 'View Analytics',
      description: 'Check your fleet performance and metrics',
      action: () => navigate('/admin/analytics'),
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: BookOpen,
      title: 'Documentation',
      description: 'Learn more about Flotto features',
      action: () => window.open('https://docs.flotto.app', '_blank'),
      color: 'bg-yellow-100 text-yellow-600'
    }
  ];

  return (
    <OnboardingLayout currentStep={5}>
      <div className="space-y-8 text-center">
        {/* Success Icon */}
        <div className="flex justify-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
        </div>

        {/* Success Message */}
        <div>
          <h2 className="text-3xl font-bold">🎉 You're All Set!</h2>
          <p className="text-gray-600 mt-3 text-lg">
            Flotto is now ready to manage your fleet operations
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6">
          {quickLinks.map((link, idx) => (
            <Card
              key={idx}
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer text-left"
              onClick={link.action}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg ${link.color} flex items-center justify-center flex-shrink-0`}>
                  <link.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold">{link.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{link.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="pt-6">
          <Button onClick={() => navigate('/dashboard')} size="lg">
            Go to Dashboard
          </Button>
        </div>

        {/* Support */}
        <Card className="p-6 bg-gray-50 border-gray-200">
          <p className="text-sm text-gray-600">
            Need help? Contact our support team at{' '}
            <a href="mailto:support@flotto.app" className="text-blue-600 hover:underline">
              support@flotto.app
            </a>
          </p>
        </Card>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingDone;
